<template>
    <div class="modal modal-todo modal-contacted fade" tabindex="-1" role="dialog" ref="modal">
        <div class="modal-dialog" role="document">
            <div class="modal-content">
                <div class="modal-header">
                    <h5 class="modal-title">Transfer Consultant</h5>
                    <button type="button" class="close" @click="closeModal" aria-label="Close">
                        <span aria-hidden="true">&times;</span>
                    </button>
                </div>
                <div class="modal-body">
                    <div v-if="isSaving" class="text-center p-4">
                        <div class="spinner-border text-primary" role="status">
                            <span class="sr-only">Loading...</span>
                        </div>
                    </div>

                    <div v-else-if="isUnavailable" class="text-center px-4 pb-4 pt-2">
                        <font-awesome-icon :icon="['far', 'calendar-exclamation']" style="height:64px; color:#F59E0B;" />
                        <p>Sorry, the selected date or time is not available. Please choose another available option.</p>
                        <button class="btn btn-submit btn-block btn-primary mt-3" @click="reschedule">Choose another schedule</button>
                        <button class="btn btn-action btn-block btn-primary mt-3" @click="justDoIt">
                            <strong>Do it anyway</strong>
                        </button>
                    </div>

                    <div v-else>
                        <form @submit.prevent="saveTransferConsultant">
                        <div class="form-group">
                            <label for="consultant">Select Consultant<span class="text-danger">*</span></label>
                            <v-select 
                                id="consultant" 
                                v-model="selectedConsultant" 
                                label="label" 
                                placeholder="Select a consultant" 
                                :options="consultantOptions" 
                                :reduce="option => option.value" 
                                :disabled="isLoadingConsultants || consultantOptions.length === 0" 
                                :clearable="false"
                                :loading="isLoadingConsultants"
                            >
                                <template #no-options>
                                    {{ isLoadingConsultants ? 'Loading consultants...' : 'No consultants available' }}
                                </template>
                            </v-select>
                        </div>
                        <datetime-picker id="transfer-consultant-date" label="Date" v-model="localDateTime" />
                        <div class="form-group">
                            <label for="transfer-consultant-comments">Comments<span class="text-danger">*</span></label>
                            <textarea id="transfer-consultant-comments" class="form-control" v-model="transferComments" rows="4" placeholder="Type here..." required></textarea>
                        </div>
                        <button type="submit" class="btn btn-submit btn-block mt-3 btn-primary" :disabled="isSaving || !isFormValid">
                            <span v-if="isSaving" class="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                            {{ isSaving ? 'Transferring...' : 'Transfer Consultant' }}
                        </button>                        </form>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import vSelect from 'vue-select';
import moment from 'moment-timezone';
import DatetimePicker from '../../../components/form/DatetimePicker.vue';
import { getConsultants } from '../../../services/api/to-do';

export default {
    name: 'TransferConsultantModal',
    components: { DatetimePicker, vSelect },
    data() {
        return {
            userId: null,
            selectedConsultant: null,
            isSaving: false,
            isUnavailable: false,
            localDateTime: '',
            transferComments: '',
            consultantOptions: [],
            isLoadingConsultants: false,
            currentItem: null,
            type: null
        }
    },
    mounted() {
        document.body.appendChild(this.$el);
    },
    computed: {
        formattedClientTime() {
            if (!this.localDateTime || !this.selectedTimezone || !this.userTimezone) {
                return '';
            }

            // First parse the date in the user's timezone, then convert to the selected timezone
            return moment.tz(this.localDateTime, this.userTimezone).tz(this.selectedTimezone).format('MMMM DD, YYYY hh:mm A');
        },
        isFormValid() {
            return this.selectedConsultant && 
                   this.transferComments.trim() && 
                   this.localDateTime;
        }
    },
    methods: {
        async loadConsultants() {
            if (!this.userId) return;
            
            this.isLoadingConsultants = true;
            try {
                const response = await getConsultants(this.userId);
                
                if (response.status === 200) {
                    this.consultantOptions = response.data.data
                        .map(consultant => ({
                            label: consultant.name,
                            value: consultant.id
                        }))
                        .sort((a, b) => a.label.localeCompare(b.label));
                }
            } catch (error) {
                console.error('Error loading consultants:', error);
                new Noty({
                    type: 'error',
                    text: 'Failed to load consultants'
                }).show();
            } finally {
                this.isLoadingConsultants = false;
            }
        },
        openModal({ item, type, user_id }) {
            this.userId = user_id;
            this.currentItem = item;
            this.selectedConsultant = null;
            this.transferComments = '';
            this.localDateTime = '';
            this.type = type;
            this.isUnavailable = false;
            this.isSaving = false;

            // Load consultants after setting the userId
            this.loadConsultants();

            this.$nextTick(() => {
                $(this.$refs.modal).modal({
                    backdrop: 'static',
                    keyboard: false
                });
            });
        },
        closeModal() {
            this.isSaving = false;
            this.isUnavailable = false;
            this.currentItem = null;
            this.selectedConsultant = null;
            this.transferComments = '';
            this.localDateTime = '';
            this.userId = null;
            this.type = null;

            $(this.$refs.modal).modal('hide');
        },
        reschedule() {
            this.isUnavailable = false;
        },
        justDoIt() {
            this.isSaving = true;
            this.saveTransferConsultant(true);
        },
        async saveTransferConsultant(forceSave = false) {
            if (!this.isFormValid) {
                new Noty({
                    type: 'error',
                    text: 'Please fill in all required fields'
                }).show();
                return;
            }

            const payload = {
                new_consultant: this.selectedConsultant,
                datetime: this.localDateTime,
                note: this.transferComments.trim(),
                force: forceSave
            };

            this.$emit('save', { id: this.currentItem?.todoable_id, type: this.type, payload, callback: this.saveResponse });
            this.isSaving = true;
        },
        saveResponse(response) {
            this.isSaving = false;

            if (response.status === 'unavailable') {
                this.isUnavailable = true;
            } else {
                this.closeModal();

                new Noty({
                    type: "success",
                    text: "Consultant transferred successfully!"
                }).show();
            }
        },
    }
}
</script>
